import Lenis from 'lenis';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

let lenis = null;
let anchorListenerBound = false;
let initialHashHandled = false;

const SCROLL_BUFFER_PX = 12;

const prefersReducedMotion = () =>
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

export function getLenis() {
    return lenis;
}

/**
 * Odstęp od góry viewportu: wysokość sticky headera + zapas (px).
 */
export function getHeaderScrollOffset() {
    const header = document.querySelector('[data-header]');
    return (header?.offsetHeight ?? 0) + SCROLL_BUFFER_PX;
}

function normalizePathname(pathname) {
    if (pathname.length > 1 && pathname.endsWith('/')) {
        return pathname.slice(0, -1);
    }

    return pathname;
}

function isExcludedAnchorNode(node) {
    return Boolean(
        node.closest('[data-header], header, footer, [data-hero-basic]')
    );
}

function getHeroBottomScrollY() {
    const hero = document.querySelector('[data-hero-basic]');

    if (!hero) {
        return 0;
    }

    return hero.getBoundingClientRect().bottom + window.scrollY;
}

/**
 * Znajduje element docelowy kotwicy; przy kolizji id preferuje section pod hero.
 *
 * @param {string} hash — np. "#sekcja"
 * @returns {HTMLElement | null}
 */
export function resolveAnchorTarget(hash) {
    const id = decodeURIComponent(hash.replace(/^#/, ''));

    if (!id) {
        return null;
    }

    const nodes = document.querySelectorAll(`[id="${CSS.escape(id)}"]`);

    if (nodes.length === 0) {
        return null;
    }

    if (nodes.length === 1) {
        return nodes[0];
    }

    const heroBottom = getHeroBottomScrollY();
    const candidates = [...nodes].filter(node => !isExcludedAnchorNode(node));

    if (candidates.length === 0) {
        return nodes[0];
    }

    const sectionsBelowHero = candidates.filter(node => {
        if (node.tagName !== 'SECTION') {
            return false;
        }

        const top = node.getBoundingClientRect().top + window.scrollY;

        return top >= heroBottom - 100;
    });

    if (sectionsBelowHero.length > 0) {
        return sectionsBelowHero[sectionsBelowHero.length - 1];
    }

    const sections = candidates.filter(node => node.tagName === 'SECTION');

    if (sections.length > 0) {
        return sections[sections.length - 1];
    }

    return candidates[0];
}

function isSamePageHashLink(href) {
    if (!href || href === '#') {
        return false;
    }

    try {
        const url = new URL(href, window.location.href);

        if (url.origin !== window.location.origin) {
            return false;
        }

        if (
            normalizePathname(url.pathname) !==
            normalizePathname(window.location.pathname)
        ) {
            return false;
        }

        if (url.search !== window.location.search) {
            return false;
        }

        return Boolean(url.hash && url.hash !== '#');
    } catch {
        return false;
    }
}

function refreshScrollTrigger() {
    ScrollTrigger.refresh();
}

function scrollToAnchorTarget(target, hash, options = {}) {
    const { immediate = false } = options;

    requestAnimationFrame(() => {
        refreshScrollTrigger();
        scrollToElement(target, getHeaderScrollOffset(), {
            lock: true,
            immediate,
            onComplete: () => {
                if (hash) {
                    history.replaceState(null, '', hash);
                }
            },
        });
    });
}

function handleAnchorClick(event) {
    const link = event.target.closest('a[href*="#"]');

    if (!link) {
        return;
    }

    const href = link.getAttribute('href');

    if (!isSamePageHashLink(href)) {
        return;
    }

    const { hash } = new URL(href, window.location.href);
    const target = resolveAnchorTarget(hash);

    if (!target) {
        return;
    }

    event.preventDefault();
    scrollToAnchorTarget(target, hash);
}

function handleInitialHash() {
    if (initialHashHandled) {
        return;
    }

    const { hash } = window.location;

    if (!hash || hash === '#') {
        return;
    }

    const run = () => {
        const target = resolveAnchorTarget(hash);

        if (!target) {
            return;
        }

        initialHashHandled = true;
        scrollToAnchorTarget(target, hash, { immediate: true });
    };

    if (document.readyState === 'complete') {
        requestAnimationFrame(() => requestAnimationFrame(run));
    } else {
        window.addEventListener(
            'load',
            () => {
                requestAnimationFrame(() => requestAnimationFrame(run));
            },
            { once: true }
        );
    }
}

function bindAnchorScroll() {
    if (anchorListenerBound) {
        return;
    }

    document.addEventListener('click', handleAnchorClick, { capture: true });
    handleInitialHash();
    anchorListenerBound = true;
}

export function initLenis() {
    bindAnchorScroll();

    if (lenis) {
        return lenis;
    }

    if (prefersReducedMotion()) {
        return null;
    }

    lenis = new Lenis({
        autoRaf: false,
        allowNestedScroll: true,
        stopInertiaOnNavigate: true,
        anchors: false,
    });

    return lenis;
}

/**
 * Przewija do elementu z uwzględnieniem sticky headera.
 *
 * @param {HTMLElement} element
 * @param {number} headerOffset — odstęp od góry viewportu (px)
 * @param {{ lock?: boolean, immediate?: boolean, onComplete?: () => void }} [options]
 */
export function scrollToElement(element, headerOffset = 0, options = {}) {
    const { lock = false, immediate = false, onComplete } = options;
    const instance = getLenis();

    if (instance) {
        instance.scrollTo(element, {
            offset: -headerOffset,
            lock,
            immediate,
            onComplete,
        });
        return;
    }

    const top =
        element.getBoundingClientRect().top + window.scrollY - headerOffset;

    window.scrollTo({
        top: Math.max(0, top),
        behavior:
            immediate || prefersReducedMotion() ? 'auto' : 'smooth',
    });

    if (onComplete) {
        if (immediate || prefersReducedMotion()) {
            onComplete();
        } else {
            window.setTimeout(onComplete, 500);
        }
    }
}